import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Lock, AlertTriangle, Check, X, ArrowRight, FlaskConical,
} from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Development Authorization Simulator (DEV only). The real partner
// authorization endpoint takes its place when it ships — no code change here.
export default function PartnerAuthorizeSim() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const state = sp.get("state") || "";
  const { user, loading } = useAuth();
  const nav = useNavigate();

  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [puid, setPuid] = useState("");
  const [consent, setConsent] = useState(false);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await api.get(`/dev/partner-authorize/${slug}/metadata`, { params: { state } });
      setMeta(data);
      setPuid(data.default_partner_user_id || "");
    } catch (e) {
      setErr(apiErr(e, "Could not load authorization request"));
    }
  }, [slug, state]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav(`/login?next=${encodeURIComponent(`/dev/partner-authorize/${slug}?state=${state}`)}`); return; }
    if (!state) { setErr("Missing authorization state."); return; }
    load();
  }, [loading, user, slug, state, load, nav]);

  const authorize = async () => {
    if (!consent) { toast.error("Please acknowledge the authorization scope."); return; }
    if (!puid.trim()) { toast.error("Enter the partner user id to authorize."); return; }
    setWorking(true);
    try {
      await api.post("/dev/partner-connect/simulator-complete", {
        state, partner_user_id: puid.trim(),
      });
      toast.success(`${meta.partner.name} connected`);
      nav("/settings?connected=1");
    } catch (e) {
      toast.error(apiErr(e, "Authorization failed"));
    } finally { setWorking(false); }
  };

  const cancel = () => nav("/settings?cancelled=1");

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[#000000]">
        <div className="w-full max-w-md rounded-2xl bg-[#0f0f0f] border border-rose-500/30 p-6 text-center" data-testid="simulator-error">
          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto mb-3" />
          <div className="font-heading text-xl font-black uppercase tracking-tight text-slate-50">Authorization unavailable</div>
          <div className="text-sm text-slate-400 mt-2">{err}</div>
          <Link to="/settings" className="inline-flex items-center gap-1.5 mt-5 text-xs font-mono uppercase tracking-wider text-[#E5B842] hover:underline">Back to Settings</Link>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[#000000] text-slate-500 font-mono text-sm uppercase tracking-widest" data-testid="simulator-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#E5B842] mr-3" /> Preparing authorization…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] text-slate-200 py-10 px-4 sm:px-6" data-testid="partner-authorize-sim">
      {/* Dev-only banner (never shown for a real production partner page) */}
      <div className="mx-auto max-w-md mb-5 flex items-center justify-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider" data-testid="sim-dev-banner">
        <FlaskConical className="w-3.5 h-3.5" /> Development Authorization Simulator
      </div>

      <div className="mx-auto max-w-md rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.22)] p-6 sm:p-7" data-testid="sim-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#E5B842]" />
          <span className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#E5B842]">{meta.partner.name}</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none" data-testid="sim-title">
          Authorize sharing
        </h1>
        <p className="text-sm text-slate-400 mt-3">
          Allow <b className="text-slate-200">{meta.partner.name}</b> to share eligible settled betting activity with
          <b className="text-slate-200"> WSTF</b> for official ranking and competitive-profile purposes.
        </p>

        <div className="mt-5 rounded-xl bg-[#0a0a0a] border border-[rgba(212,175,55,0.16)] p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">What will be shared</div>
          <ul className="text-sm text-slate-300 space-y-1.5">
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Settled event results (W/L/VOID/PUSH)</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Market/subtype metadata for ranking separation</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Your partner user id (for identity binding)</li>
          </ul>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">What will <span className="text-rose-300">never</span> be shared</div>
          <ul className="text-sm text-slate-400 space-y-1.5">
            <li className="flex gap-2"><X className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /> Wallet balance, deposits, withdrawals</li>
            <li className="flex gap-2"><X className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /> Payment methods, KYC documents, limits</li>
            <li className="flex gap-2"><X className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /> Password, email, phone, address</li>
          </ul>
        </div>

        <div className="mt-5">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Partner user id (fixture)</label>
          <input data-testid="sim-puid-input" value={puid} onChange={(e) => setPuid(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 font-mono"
            placeholder="assos365-u-xxxx" />
          <div className="text-[10px] font-mono text-slate-600 mt-1 flex items-center gap-1"><Lock className="w-3 h-3" /> You are {meta.wsbf_player_id} — only this authorization is bound to you</div>
        </div>

        <label className="mt-4 flex items-start gap-2 cursor-pointer" data-testid="sim-consent-label">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sim-consent-checkbox"
            className="mt-1 h-4 w-4 accent-[#E5B842]" />
          <span className="text-xs text-slate-400">I understand WSTF will receive settled activity attribution and never any account credentials or financial data.</span>
        </label>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={cancel} data-testid="sim-cancel-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-slate-100"
            style={{ transition: "color 0.15s ease" }}>Cancel</button>
          <button onClick={authorize} disabled={working || !consent}
            data-testid="sim-authorize-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#E5B842] text-black disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#C99E30]"
            style={{ transition: "background-color 0.15s ease" }}>
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Authorize
          </button>
        </div>
      </div>
      <div className="mx-auto max-w-md mt-4 text-center text-[10px] font-mono uppercase tracking-wider text-slate-600" data-testid="sim-footer">
        Simulator in place until {meta.partner.name} authorization endpoint goes live
      </div>
    </div>
  );
}
